import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { usePlanLimits } from "../../contexts/PlanLimitContext";
import JobPreviewModal from "../jobs/JobPreviewModal";

// Job details overlay for the Financial Reports page. Opens the full
// interactive JobPreviewModal (attachments, reminders, send-to-tech, delete)
// WITHOUT navigating away, so report filters and scroll position survive.
// Edit navigates to the full-page job form with returnTo=/dashboard/financials.
const FinancialsJobPreview = ({ jobId, onClose, onChanged }) => {
  const navigate = useNavigate();
  const { isSmsReadyAsync } = usePlanLimits();
  const [job, setJob] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [sendingToTech, setSendingToTech] = useState(null);
  const [sendingReview, setSendingReview] = useState(null);

  const authCfg = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    if (!jobId) { setJob(null); return; }
    axios.get(`${API}/jobs/${jobId}`, authCfg())
      .then(r => setJob(r.data))
      .catch(() => { toast.error("Failed to load job details"); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  useEffect(() => {
    axios.get(`${API}/settings/payment-types`, authCfg())
      .then(r => setPaymentTypes(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  }, []);

  const handleEdit = (j) => {
    navigate(`/dashboard/jobs/${j.job_id}/edit`, { state: { returnTo: "/dashboard/financials" } });
  };

  const handleDelete = async (j) => {
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/jobs/${j.job_id}`, authCfg());
      toast.success("Job deleted");
      onClose();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete job");
    }
  };

  const handleSendReminder = async (j) => {
    if (!(await isSmsReadyAsync())) return;
    try {
      await axios.post(`${API}/notifications/send-reminder`, {
        job_id: j.job_id,
        customer_phone: j.customer_phone,
        customer_name: j.customer_name,
        job_title: j.title,
        scheduled_date: j.scheduled_date,
        scheduled_time: j.scheduled_time,
        address: j.customer_address,
      }, authCfg());
      toast.success("Reminder sent successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send reminder");
    }
  };

  const handleSendToTech = async (j) => {
    if (!j.assigned_to) { toast.error("No technician assigned to this job"); return; }
    if (!(await isSmsReadyAsync())) return;
    setSendingToTech(j.job_id);
    try {
      const res = await axios.post(`${API}/technicians/jobs/${j.job_id}/send-assignment`, {}, authCfg());
      if (res.data?.masking_warning) toast.warning(res.data.masking_warning, { duration: 9000 });
      toast.success("Job details sent to technician");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send to technician");
    } finally {
      setSendingToTech(null);
    }
  };

  const handleSendReviewRequest = async (j) => {
    if (!(await isSmsReadyAsync())) return;
    setSendingReview(j.job_id);
    try {
      await axios.post(`${API}/jobs/${j.job_id}/send-review-request`, {}, authCfg());
      toast.success("Review request sent to customer");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send review request");
    } finally {
      setSendingReview(null);
    }
  };

  if (!jobId || !job) return null;

  return (
    <JobPreviewModal
      job={job}
      onClose={onClose}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onSendReminder={handleSendReminder}
      onSendToTech={handleSendToTech}
      onSendReviewRequest={handleSendReviewRequest}
      sendingToTech={sendingToTech}
      sendingReview={sendingReview}
      paymentTypes={paymentTypes}
    />
  );
};

export default FinancialsJobPreview;
